import { createShortlistStore } from './storage'
import { bindShortlistSync } from './sync'
import { trackShortlistAction } from './tracking'

const initializedRoots = new WeakSet()

function cloneTemplate(root, selector) {
  const template = root.querySelector(selector)
  if (!(template instanceof HTMLTemplateElement)) return null
  return template.content.firstElementChild?.cloneNode(true) ?? null
}

function setMessage(element, message) {
  if (!(element instanceof HTMLElement)) return
  element.textContent = message
  element.hidden = message === ''
}

function storageFailureMessage(reason) {
  return reason === 'unsupported-schema'
    ? 'いいねした内容を読み込めませんでした。ページを再読み込みして、もう一度お試しください。'
    : 'いいねした事例を読み込めません。ブラウザのデータ保存設定をご確認のうえ、もう一度お試しください。'
}

function renderCardImage(frame, path, alt) {
  const image = frame.querySelector('[data-shortlist-card-image]')
  const badge = frame.querySelector('.reform-case-before-after-badge')
  const placeholder = frame.querySelector('[data-shortlist-card-image-placeholder]')
  const placeholderLabel = frame.querySelector('[data-shortlist-card-image-placeholder-label]')
  if (!(image instanceof HTMLImageElement) || !(placeholder instanceof HTMLElement)) return false

  frame.hidden = path === null
  if (badge instanceof HTMLElement) badge.hidden = path === null
  if (path === null) return false

  const showImage = () => {
    image.hidden = false
    image.classList.remove('opacity-0')
    placeholder.hidden = true
  }
  image.addEventListener('load', showImage, { once: true })
  image.addEventListener('error', () => {
    image.hidden = true
    image.classList.add('opacity-0')
    placeholder.hidden = false
    if (placeholderLabel instanceof HTMLElement) placeholderLabel.textContent = '写真は事例ページで確認できます'
  }, { once: true })
  image.hidden = false
  image.src = path
  image.alt = alt
  if (image.complete && image.naturalWidth > 0) showImage()
  return true
}

function renderCardImages(card, entry) {
  const grid = card.querySelector('[data-shortlist-card-image-grid]')
  const beforeFrame = card.querySelector('[data-shortlist-card-image-frame="before"]')
  const afterFrame = card.querySelector('[data-shortlist-card-image-frame="after"]')
  if (!(grid instanceof HTMLElement) || !(beforeFrame instanceof HTMLElement) || !(afterFrame instanceof HTMLElement)) return

  const beforeVisible = renderCardImage(
    beforeFrame,
    entry.snapshot.beforeImagePath,
    `${entry.snapshot.title}のリフォーム前写真`,
  )
  const afterVisible = renderCardImage(
    afterFrame,
    entry.snapshot.imagePath,
    `${entry.snapshot.title}のリフォーム後写真`,
  )
  const imageCount = Number(beforeVisible) + Number(afterVisible)
  grid.classList.toggle('grid-cols-1', imageCount < 2)
  grid.classList.toggle('grid-cols-2', imageCount === 2)

  if (imageCount === 0) {
    afterFrame.hidden = false
    const placeholderLabel = afterFrame.querySelector('[data-shortlist-card-image-placeholder-label]')
    if (placeholderLabel instanceof HTMLElement) placeholderLabel.textContent = '写真は事例ページで確認できます'
  }
}

function renderCard(root, entry) {
  const card = cloneTemplate(root, '[data-shortlist-card-template]')
  if (!(card instanceof HTMLElement)) return null
  card.dataset.shortlistCaseId = String(entry.caseId)

  card.querySelectorAll('[data-shortlist-case-link]').forEach((link) => {
    if (link instanceof HTMLAnchorElement) link.href = entry.path
  })

  const title = card.querySelector('[data-shortlist-case-title]')
  const companyName = card.querySelector('[data-shortlist-company-name]')
  const overview = card.querySelector('[data-shortlist-card-overview]')
  const cost = card.querySelector('[data-shortlist-cost]')
  const period = card.querySelector('[data-shortlist-period]')
  const parts = card.querySelector('[data-shortlist-parts]')
  const partsValue = card.querySelector('[data-shortlist-parts-value]')
  if (title instanceof HTMLElement) title.textContent = entry.snapshot.title
  if (companyName instanceof HTMLElement) companyName.textContent = entry.snapshot.companyName
  if (overview instanceof HTMLElement) {
    overview.hidden = entry.snapshot.cost === null && entry.snapshot.period === ''
  }
  if (cost instanceof HTMLElement) {
    cost.textContent = entry.snapshot.cost === null ? '―' : `${entry.snapshot.cost.toLocaleString('ja-JP')}万円`
  }
  if (period instanceof HTMLElement) period.textContent = entry.snapshot.period || '―'
  if (parts instanceof HTMLElement) parts.hidden = entry.snapshot.parts.length === 0
  if (partsValue instanceof HTMLElement) partsValue.textContent = entry.snapshot.parts.join('、')
  renderCardImages(card, entry)

  return card
}

function renderCases(root, cases) {
  const container = root.querySelector('[data-shortlist-cards]')
  if (container instanceof HTMLElement) {
    container.replaceChildren(...cases.map((entry) => renderCard(root, entry)).filter(Boolean))
  }
}

function renderWorkspace(root, store) {
  const error = root.querySelector('[data-shortlist-workspace-error]')
  const empty = root.querySelector('[data-shortlist-empty]')
  const content = root.querySelector('[data-shortlist-content]')
  const count = root.querySelector('[data-shortlist-saved-count]')
  const result = store.read()

  if (!result.ok) {
    setMessage(error, storageFailureMessage(result.reason))
    if (empty instanceof HTMLElement) empty.hidden = true
    if (content instanceof HTMLElement) content.hidden = true
    return result
  }

  setMessage(error, '')
  if (count instanceof HTMLElement) count.textContent = String(result.count)
  if (result.count === 0) {
    if (empty instanceof HTMLElement) empty.hidden = false
    if (content instanceof HTMLElement) content.hidden = true
    return result
  }

  if (empty instanceof HTMLElement) empty.hidden = true
  if (content instanceof HTMLElement) content.hidden = false
  renderCases(root, [...result.cases].sort((left, right) => right.savedAt - left.savedAt))
  return result
}

export function initReformCaseShortlistWorkspace({ store = createShortlistStore() } = {}) {
  document.querySelectorAll('[data-reform-case-shortlist-workspace]').forEach((root) => {
    if (!(root instanceof HTMLElement) || initializedRoots.has(root)) return
    initializedRoots.add(root)
    const surface = root.dataset.reformCaseShortlistSurface
    let openedTracked = false

    const sync = () => {
      const result = renderWorkspace(root, store)
      if (openedTracked || !result?.ok) return

      openedTracked = true
      trackShortlistAction('reform_case_shortlist_opened', { surface, count: result.count })
    }

    root.addEventListener('click', (event) => {
      const clicked = event.target instanceof Element ? event.target : null
      const caseLink = clicked?.closest('[data-shortlist-case-link]')
      if (caseLink instanceof HTMLAnchorElement) {
        const caseId = Number(caseLink.closest('[data-shortlist-card]')?.dataset.shortlistCaseId)
        const result = store.read()
        if (Number.isSafeInteger(caseId) && caseId > 0 && result.ok) {
          trackShortlistAction('reform_case_shortlist_case_opened', { caseId, surface, count: result.count })
        }
      }
    })

    bindShortlistSync(root, sync)
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initReformCaseShortlistWorkspace())
} else {
  initReformCaseShortlistWorkspace()
}
